import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Accordion from '../../../../Accordion/Accordion';
import Heading from '../../../../Typography/Heading/Heading';
import Line from '../../../../Line/Line';
import Paragraph from '../../../../Typography/Paragraph/Paragraph';
import Icon from '../../../../Icon2/Icon';
import {
  formatDate,
  formatFileSize,
  getAssetTypeDisplay,
  isGroupValue,
  getChildrenCount,
  formatMetadataValue,
  hasMetadata,
  UI_CONSTANTS,
  calculatePopoverPosition,
} from './utils';
import './GalleryView.style.scss';
import { AssetData } from '../../../utils/types';

interface PopoverState {
  isOpen: boolean;
  position: { top: number; left: number };
  currentGroup: Record<string, any> | null;
  navigationStack: Array<{ data: Record<string, any>; title: string }>;
  currentTitle: string;
}

interface AssetInfoSidebarProps {
  selectedAssets?: AssetData[];
  totalAssets?: number;
  assetTypeSchemaMap?: Map<string, { title: string }>;
  localesWithFallback?: any[];
  userDisplayNameMap?: Record<string, string>;
  spacesWithTitles?: Array<{ uid: string; title: string }>;
}

interface FieldConfig {
  key: string;
  label: string;
  getValue: (asset: AssetData) => string;
  className?: string;
}

const FieldItem: React.FC<{
  label: string;
  value: string;
  isMixed?: boolean;
  className?: string;
}> = React.memo(({ label, value, isMixed = false, className = '' }) => (
  <div className={`AssetInfoSidebar__field-item ${className}`}>
    <Paragraph variant="p3" text={label} className="AssetInfoSidebar__field-label" />
    <Paragraph
      text={value}
      variant="p3"
      variantStyle="regular"
      className={`AssetInfoSidebar__field-value ${isMixed ? 'AssetInfoSidebar__field-value--mixed' : ''}`}
    />
  </div>
));

FieldItem.displayName = 'FieldItem';

const TagsDisplay: React.FC<{
  tags?: string[];
  hasMultipleAssets: boolean;
}> = React.memo(({ tags, hasMultipleAssets }) => {
  if (hasMultipleAssets) {
    return (
      <Paragraph
        text="Mixed"
        variant="p3"
        variantStyle="regular"
        className="AssetInfoSidebar__field-value AssetInfoSidebar__field-value--mixed"
      />
    );
  }

  if (!tags?.length) {
    return (
      <Paragraph
        text="-"
        variant="p3"
        variantStyle="regular"
        className="AssetInfoSidebar__field-value"
      />
    );
  }

  return (
    <div className="AssetInfoSidebar__tags">
      {tags?.map((tag, index) => (
        <span key={index} className="AssetInfoSidebar__tag-pill">{tag}</span>
      ))}
    </div>
  );
});

TagsDisplay.displayName = 'TagsDisplay';

const GroupItem: React.FC<{
  itemKey: string;
  value: any;
  onGroupClick: (event: React.MouseEvent, groupData: Record<string, any>, groupKey: string) => void;
  isNested?: boolean;
  assetTypeSchemaMap?: Map<string, { title: string }>;
}> = React.memo(({ itemKey, value, onGroupClick, isNested = false, assetTypeSchemaMap }) => {
  const keyTitle = assetTypeSchemaMap && assetTypeSchemaMap.has(itemKey)
    ? assetTypeSchemaMap.get(itemKey)?.title || itemKey
    : itemKey;


  if (isGroupValue(value)) {
    const handleClick = (e: React.MouseEvent) => {
      onGroupClick(e, value, itemKey);
    };

    return (
      <div className={isNested ? 'MetadataPopover__group-item' : 'AssetInfoSidebar__metadata-item--group'} onClick={handleClick}>
        <div className={isNested ? 'MetadataPopover__group-header' : 'AssetInfoSidebar__metadata-group-header'}>
          <div className={isNested ? 'MetadataPopover__group-title' : 'AssetInfoSidebar__metadata-group-title'}>
            <Heading
              tagName="h6"
              text={keyTitle}
              className={isNested ? 'MetadataPopover__group-key' : 'AssetInfoSidebar__metadata-key'}
            />
            <span className="GroupBadge">{getChildrenCount(value)}</span>
          </div>
          <Icon version="v2" icon="ArrowsOutSimple" size="tiny" className="AssetInfoSidebar__metadata-group-icon" />
        </div>
      </div>
    );
  }

  return (
    <div className={isNested ? 'MetadataPopover__single-item' : ''}>
      <Heading
        tagName="h6"
        text={keyTitle}
        className={isNested ? 'MetadataPopover__key' : 'AssetInfoSidebar__metadata-key'}
      />
      <Paragraph
        text={formatMetadataValue(value)}
        variant="p3"
        variantStyle="regular"
        className={isNested ? 'MetadataPopover__value' : 'AssetInfoSidebar__metadata-value'}
      />
    </div>
  );
});

GroupItem.displayName = 'GroupItem';

const CustomMetadata: React.FC<{
  metadata?: Record<string, any>;
  hasMultipleAssets: boolean;
  onGroupClick: (event: React.MouseEvent, groupData: Record<string, any>, groupKey: string) => void;
  assetTypeSchemaMap?: Map<string, { title: string }>;
}> = React.memo(({ metadata, hasMultipleAssets, onGroupClick, assetTypeSchemaMap }) => {
  if (hasMultipleAssets) {
    return (
      <Paragraph
        text="Mixed"
        variant="p3"
        variantStyle="regular"
        className="AssetInfoSidebar__field-value AssetInfoSidebar__field-value--mixed"
      />
    );
  }

  if (!hasMetadata(metadata)) {
    return (
      <Paragraph
        text="-"
        variant="p3"
        variantStyle="regular"
        className="AssetInfoSidebar__field-value"
      />
    );
  }

  const metadataEntries = Object.entries(metadata!);

  return (
    <div className="AssetInfoSidebar__metadata">
      {metadataEntries.map(([key, value], index) => (
        <React.Fragment key={key}>
          <div className={`AssetInfoSidebar__metadata-item ${isGroupValue(value) ? 'AssetInfoSidebar__metadata-item--group' : ''}`}>
            <GroupItem
              itemKey={key}
              value={value}
              onGroupClick={onGroupClick}
              isNested={false}
              assetTypeSchemaMap={assetTypeSchemaMap}
            />
          </div>
          {index < metadataEntries.length - 1 && <Line type="solid" />}
        </React.Fragment>
      ))}
    </div>
  );
});

CustomMetadata.displayName = 'CustomMetadata';

const MetadataPopover: React.FC<{
  popoverState: PopoverState;
  onClose: () => void;
  onBack: () => void;
  onNestedGroupClick: (groupData: Record<string, any>, groupKey: string) => void;
  assetTypeSchemaMap?: Map<string, { title: string }>;
}> = React.memo(({ popoverState, onClose, onBack, onNestedGroupClick, assetTypeSchemaMap }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const schemaMap = assetTypeSchemaMap ?? new Map();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (popoverState.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [popoverState.isOpen, onClose]);

  if (!popoverState.isOpen || !popoverState.currentGroup) return null;

  const renderGroupContent = (
    groupData: Record<string, any>,
    assetTypeSchemaMap: Map<string, { title: string }>
  ): React.ReactNode[] => {
    return Object.entries(groupData).map(([key, value]) => (
      <div key={key} className="MetadataPopover__item">
        <GroupItem
          itemKey={key}
          value={value}
          onGroupClick={(_: any, data: Record<string, any>, groupKey: string): void => onNestedGroupClick(data, groupKey)}
          isNested
          assetTypeSchemaMap={assetTypeSchemaMap}
        />
      </div>
    ));
  };

  return createPortal(
    <div
      ref={popoverRef}
      className="MetadataPopover__portal"
      style={{
        position: 'fixed',
        top: `${popoverState.position.top}px`,
        left: `${popoverState.position.left}px`,
        zIndex: 9999
      }}
    >
      <div className="MetadataPopover">
        <div className="MetadataPopover__header">
          <div className="MetadataPopover__left-section">
            {popoverState.navigationStack.length > 0 && (
              <button className="MetadataPopover__back-btn" onClick={onBack} type="button">
                <Icon version="v2" icon="CaretLeft" size="small" />
              </button>
            )}
            <div className="MetadataPopover__title-section">
              <Heading tagName="h6" text={(schemaMap).has(popoverState.currentTitle) ? (schemaMap).get(popoverState.currentTitle)?.title : popoverState.currentTitle} className="MetadataPopover__title" />
              <span className="MetadataPopover__title-badge">{getChildrenCount(popoverState.currentGroup)}</span>
            </div>
          </div>
          <button className="MetadataPopover__close-btn" onClick={onClose} type="button">
            <Icon version="v2" icon="Cancel" size="small" />
          </button>
        </div>
        <div className="MetadataPopover__content">
          {renderGroupContent(popoverState.currentGroup, schemaMap)}
        </div>
      </div>
    </div>,
    document.body
  );
});

MetadataPopover.displayName = 'MetadataPopover';

const AssetInfoSidebar: React.FC<AssetInfoSidebarProps> = ({
  selectedAssets = [],
  assetTypeSchemaMap,
  localesWithFallback = [],
  userDisplayNameMap = {},
  spacesWithTitles = [],
}) => {
  const [popoverState, setPopoverState] = useState<PopoverState>({
    isOpen: false,
    position: { top: 0, left: 0 },
    currentGroup: null,
    navigationStack: [],
    currentTitle: ''
  });

  const hasMultipleAssets = useMemo(() => selectedAssets.length > 1, [selectedAssets.length]);
  const asset = useMemo(() => selectedAssets[0] || {}, [selectedAssets]) as AssetData;

  const getFieldValue = useCallback((getValue: () => string) => {
    return hasMultipleAssets ? 'Mixed' : getValue();
  }, [hasMultipleAssets]);

  const systemFields = useMemo((): FieldConfig[] => [
    {
      key: "fileName",
      label: "File Name",
      getValue: (asset) => asset.file_name || '-'
    },
    {
      key: 'assetType',
      label: 'Asset Type',
      getValue: (asset) => getAssetTypeDisplay(asset)
    },
    {
      key: 'space',
      label: 'Space',
      getValue: (asset) => {
        if (!asset.space_uid || !spacesWithTitles.length) return '-';
        const space = spacesWithTitles.find(s => s.uid === asset.space_uid);
        return space?.title || '-';
      }
    },
    {
      key: 'fileSize',
      label: 'File Size',
      getValue: (asset) => formatFileSize(asset.file_size)
    },
    {
      key: 'contentType',
      label: 'Content Type',
      getValue: (asset) => asset.content_type || '-'
    },
    {
      key: 'lastModified',
      label: 'Last Modified At',
      getValue: (asset) => formatDate(asset.updated_at)
    },
    {
      key: 'createdBy',
      label: 'Created By',
      getValue: (asset) => userDisplayNameMap?.[asset.created_by] || asset.created_by || '-'
    },
    {
      key: 'modifiedBy',
      label: 'Modified By',
      getValue: (asset) => userDisplayNameMap?.[asset.updated_by] || asset.updated_by || '-'
    },
    {
      key: 'createdAt',
      label: 'Created At',
      getValue: (asset) => formatDate(asset.created_at)
    },
    {
      key: 'Dimensions',
      label: 'Dimensions',
      getValue: (asset) => asset.dimensions
        ? `${asset.dimensions.width} × ${asset.dimensions.height} pixels`
        : "-"
    },
    {
      key: 'Language',
      label: 'Language',
      getValue: (asset) => localesWithFallback?.find((locale: any) => locale?.code === asset?.locale)?.name || '-'
    }
  ], []);

  const handleGroupClick = useCallback((
    event: React.MouseEvent,
    groupData: Record<string, any>,
    groupKey: string
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();

    const position = calculatePopoverPosition(rect, UI_CONSTANTS.POPOVER_WIDTH);

    setPopoverState({
      isOpen: true,
      position,
      currentGroup: groupData,
      navigationStack: [],
      currentTitle: groupKey
    });
  }, []);

  const handleNestedGroupClick = useCallback((
    groupData: Record<string, any>,
    groupKey: string
  ) => {
    setPopoverState(prev => ({
      ...prev,
      currentGroup: groupData,
      navigationStack: [...prev.navigationStack, { data: prev.currentGroup!, title: prev.currentTitle }],
      currentTitle: groupKey
    }));
  }, []);

  const handleBackClick = useCallback(() => {
    setPopoverState(prev => {
      const newStack = [...prev.navigationStack];
      const parent = newStack.pop();

      if (parent) {
        return {
          ...prev,
          currentGroup: parent.data,
          navigationStack: newStack,
          currentTitle: parent.title
        };
      } else {
        return {
          ...prev,
          isOpen: false,
          currentGroup: null,
          navigationStack: [],
          currentTitle: ''
        };
      }
    });
  }, []);

  const closePopover = useCallback(() => {
    setPopoverState({
      isOpen: false,
      position: { top: 0, left: 0 },
      currentGroup: null,
      navigationStack: [],
      currentTitle: ''
    });
  }, []);

  return (
    <div className="AssetInfoSidebar">
      <div className="AssetInfoSidebar__heading">
        <Heading className="AssetInfoSidebar__heading-text" tagName="h6" text="Asset Information" />
      </div>

      <div className="AssetInfoSidebar__sections">
        <Accordion
          version="v2"
          isContainerization={false}
          title="General"
          defaultExpanded={true}
        >
          <div className="AssetInfoSidebar__section-content">
            <div className="AssetInfoSidebar__field-group">
              <FieldItem
                label="Title"
                value={getFieldValue(() => asset.title || '-')}
                isMixed={hasMultipleAssets}
              />

              <Line type="solid" />

              <FieldItem
                label="Description"
                value={getFieldValue(() => asset.description || '-')}
                isMixed={hasMultipleAssets}
              />

              <Line type="solid" />

              <div className="AssetInfoSidebar__field-item">
                <Paragraph variant="p3" text="Tags" className="AssetInfoSidebar__field-label" />
                <div className="AssetInfoSidebar__field-value">
                  <TagsDisplay tags={asset.tags} hasMultipleAssets={hasMultipleAssets} />
                </div>
              </div>
            </div>
          </div>
        </Accordion>

        <Accordion
          version="v2"
          isContainerization={false}
          title="System Metadata"
          defaultExpanded={true}
        >
          <div className="AssetInfoSidebar__section-content">
            <div className="AssetInfoSidebar__field-group">
              {Array.from({ length: Math.ceil(systemFields.length / 2) }, (_, rowIndex) => {
                const startIndex = rowIndex * 2;
                const fieldsInRow = systemFields.slice(startIndex, startIndex + 2);

                return (
                  <div key={rowIndex} className="AssetInfoSidebar__field-row">
                    {fieldsInRow.map(field => (
                      <FieldItem
                        key={field.key}
                        label={field.label}
                        value={getFieldValue(() => field.getValue(asset))}
                        isMixed={hasMultipleAssets}
                        className="AssetInfoSidebar__field-item--half-width"
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </Accordion>

        <Accordion
          version="v2"
          isContainerization={false}
          title="User Defined Metadata"
          defaultExpanded={false}
        >
          <div className="AssetInfoSidebar__section-content">
            <div className="AssetInfoSidebar__field-group">
              <CustomMetadata
                metadata={asset.user_defined_fields}
                hasMultipleAssets={hasMultipleAssets}
                onGroupClick={handleGroupClick}
                assetTypeSchemaMap={assetTypeSchemaMap}
              />
            </div>
          </div>
        </Accordion>
      </div>

      <MetadataPopover
        popoverState={popoverState}
        onClose={closePopover}
        onBack={handleBackClick}
        onNestedGroupClick={handleNestedGroupClick}
        assetTypeSchemaMap={assetTypeSchemaMap}
      />
    </div>
  );
};

export default React.memo(AssetInfoSidebar);